import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

/**
 * Google AdSense Component
 * 
 * Usage:
 * <AdSense slot="homePageTop" />
 * <AdSense slot="homePageSidebar" />
 * <AdSense slot="homePageBottom" />
 * <AdSense slot="inFeed" />
 * <AdSense slot="inArticle" />
 */

const AdSense = ({ slot, className = '', style = {} }) => {
  const [adSettings, setAdSettings] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    fetchAdSettings();
  }, []);

  const fetchAdSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/adsense/settings`);
      setAdSettings(response.data);
    } catch (error) {
      console.error('Error fetching AdSense settings:', error);
    }
  };

  useEffect(() => {
    // Load AdSense script if enabled
    if (adSettings?.isEnabled && adSettings?.publisherId && !isLoaded) {
      const script = document.createElement('script');
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adSettings.publisherId}`;
      script.async = true;
      script.crossOrigin = 'anonymous';
      
      script.onload = () => {
        setIsLoaded(true);
        // Initialize auto ads if enabled
        if (adSettings.autoAdsEnabled) {
          try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
          } catch (e) {
            console.error('AdSense auto ads error:', e);
          }
        }
      };

      document.head.appendChild(script);

      return () => {
        // Cleanup script on unmount
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };
    }
  }, [adSettings, isLoaded]);

  useEffect(() => {
    // Push ad after settings are loaded and script is ready
    if (isLoaded && adSettings?.isEnabled && adSettings?.adSlots?.[slot]?.enabled) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        console.error('AdSense ad push error:', e);
      }
    }
  }, [isLoaded, adSettings, slot]);

  // Don't render if AdSense is not enabled or settings not loaded
  if (!adSettings?.isEnabled || !adSettings?.publisherId) {
    return null;
  }

  // Don't render if this specific slot is not enabled
  const slotConfig = adSettings?.adSlots?.[slot];
  if (!slotConfig?.enabled || !slotConfig?.slotId) {
    return null;
  }

  // Get ad format
  const adFormat = slotConfig.format || 'auto';
  const isFluid = adFormat === 'fluid';

  return (
    <div className={`adsense-container ${className}`} style={{ textAlign: 'center', margin: '20px 0', ...style }}>
      <ins
        className="adsbygoogle"
        style={{
          display: 'block',
          ...(isFluid && { width: '100%', height: 'auto' })
        }}
        data-ad-client={adSettings.publisherId}
        data-ad-slot={slotConfig.slotId}
        data-ad-format={adFormat}
        data-full-width-responsive={adFormat === 'auto' || isFluid ? 'true' : 'false'}
      ></ins>
    </div>
  );
};

export default AdSense;
